import { ref, computed, provide, watchEffect } from 'vue';
import { PROFILE_BG_GRADIENTS } from '../component-lib.esm.js';
import Logo from '../../components/logo/component-lib.esm.js';
import MapMarkerIcon from '../../icons/component-lib.esm99.js';
import SocialAccounts from './component-lib.esm5.js';
import { getLinkType } from '../component-lib.esm3.js';
import ProfileGroup from '../group/component-lib.esm2.js';
import { getBackgroundImageUrlsFromMediaUrl, convertImageUrlToEncodedUrl } from '../../utils/component-lib.esm19.js';
import { checkLinkEnabled } from '../../utils/component-lib.esm20.js';
import { useTheme } from '../../compositions/component-lib.esm3.js';
import { useStyleTag } from '../../compositions/component-lib.esm10.js';
import { getGoogleFontUrl } from '../../utils/component-lib.esm18.js';

//
var script = {
  name: 'PreviewProfile',
  components: {
    Logo,
    MapMarkerIcon,
    SocialAccounts,
    ProfileGroup
  },
  props: {
    profile: {
      type: Object,
      required: true
    },
    links: {
      type: Array,
      default: () => []
    },
    socialAccounts: {
      type: Array,
      required: true
    },
    defaultProfileImage: {
      type: String,
      required: true
    },
    isFreePlan: Boolean
  },
  setup(props) {
    const container = ref(null);
    const linkRefs = ref(null);
    const theme = useTheme();
    const styleTag = useStyleTag();
    const profile = computed(() => {
      return {
        ...props.profile,
        isFreePlan: props.isFreePlan
      };
    });
    provide('profile', profile);
    provide('scrollRoot', container);
    const logoProps = computed(() => {
      return {
        color: theme.value.profile.logoFill,
        markColor: theme.value.profile.markFill
      };
    });
    const classes = computed(() => {
      const settings = props.profile.theme ? props.profile.theme : props.profile;
      return {
        'site-container--has-gradient': settings.site_bg_style === 'gradient',
        'site-container--has-animated-gradient': settings.site_bg_animated,
        'site-container--has-bg-image': settings.site_bg_style === 'image',
        'site-container--has-bg-pattern': settings.site_bg_style === 'pattern'
      };
    });
    const username = computed(() => {
      return props.profile.site_intro_title ? props.profile.site_intro_title : '@' + (props.profile.username ? props.profile.username : '');
    });
    const containerStyles = computed(() => {
      let styles = {};
      const settings = props.profile.theme ? props.profile.theme : props.profile;
      if (props.profile.site_bg_style === 'image' && props.profile.site_bg_image && (props.profile.site_bg_image.mobile || props.profile.site_bg_image.media_url)) {
        const {
          mobile,
          tablet,
          desktop
        } = props.profile.site_bg_image.media_url ? getBackgroundImageUrlsFromMediaUrl(props.profile.site_bg_image.media_url) : props.profile.site_bg_image;
        let gradient;
        if (props.profile.site_bg_image_filter) {
          gradient = PROFILE_BG_GRADIENTS[props.profile.site_bg_image_filter.toUpperCase()];
        }
        styles = {
          '--bg-mobile-src': `${gradient ? `${gradient}, ` : ''}url('${mobile}')`,
          '--bg-tablet-src': `${gradient ? `${gradient}, ` : ''}url('${tablet}')`,
          '--bg-desktop-src': `${gradient ? `${gradient}, ` : ''}url('${desktop}')`
        };
      }
      if (settings.site_bg_style === 'gradient') {
        styles = {
          '--p-bg-gradient': `linear-gradient(${settings.site_bg_gradient_direction}, ${settings.site_bg_colors.join(',')})`
        };
      }
      return {
        ...styles
      };
    });
    const locationComponent = computed(() => {
      if (!props.isFreePlan && props.profile.site_location_url) {
        return 'a';
      }
      return 'span';
    });
    const locationAttrs = computed(() => {
      if (!props.isFreePlan && props.profile.site_location_url) {
        return {
          href: props.profile.site_location_url,
          target: '_blank',
          rel: 'noopener ugc'
        };
      }
      return {};
    });
    const profileImage = computed(() => {
      return props.profile.profile_image ? convertImageUrlToEncodedUrl(props.profile.profile_image, 160) : props.defaultProfileImage;
    });
    const siteHeaderClasses = computed(() => {
      return {
        'site__header--has-profile-image': !props.profile.site_hide_profile_image,
        'site__header--align-left': props.profile.site_header_layout === 'left-aligned'
      };
    });
    const showSiteHeader = computed(() => {
      return !props.profile.site_hide_profile_image || !props.profile.site_hide_intro_title || !!props.profile.site_location || !!props.profile.site_intro_text;
    });
    watchEffect(() => {
      if (!styleTag.value || !profile.value.site_font_family_details) return;
      const fontUrls = [];
      const settings = profile.value.theme ? profile.value.theme : profile.value;
      const {
        site_font_family_details,
        site_button_font_family_details,
        site_title_font_family_details = {},
        site_title_inherit = true
      } = settings;
      if (site_font_family_details.is_google_font) {
        fontUrls.push(getGoogleFontUrl(site_font_family_details.family, site_font_family_details.variants));
      }
      if (site_button_font_family_details.is_google_font) {
        fontUrls.push(getGoogleFontUrl(site_button_font_family_details.family, site_button_font_family_details.variants));
      }
      if (!site_title_inherit && site_title_font_family_details && site_title_font_family_details.is_google_font) {
        fontUrls.push(getGoogleFontUrl(site_title_font_family_details.family, site_title_font_family_details.variants));
      }
      styleTag.value.innerHTML = `
        ${Array.from(new Set(fontUrls)).map(url => `@import url('${url}');`).join('\n')}
      `;
    });
    function scrollToLink(id) {
      if (!linkRefs.value) return null;
      const linkComponent = linkRefs.value.find(c => id === c.id);
      if (linkComponent) {
        linkComponent.$el.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center'
        });
      }
      return linkComponent;
    }
    return {
      container,
      classes,
      getLinkType,
      checkLinkEnabled,
      linkRefs,
      scrollToLink,
      profileImage,
      username,
      containerStyles,
      locationComponent,
      locationAttrs,
      showSiteHeader,
      siteHeaderClasses,
      logoProps
    };
  }
};
export { script as default };